// Arabic (and English source) strings for the public Halal suite: screener, stock verdict,
// sector leaderboards, Chart Lens, the Shariah Supervisory Board and Learn.
// Backend prose that ships as seeded English (rulings, methodology, mandate) is mirrored here
// by slug/stage so the page can render it in Arabic without a second data model.

export const HALAL_I18N = {
  en: {
    // shared
    hl_back: "Back",
    hl_read_methodology: "Read the methodology",
    hl_open_account: "Open your account",
    hl_all_markets: "All markets",
    hl_market: "Market",

    // screener
    scr_eyebrow: "Free halal screener",
    scr_h1a: "Top halal stocks,",
    scr_h1b: "screened in the open.",
    scr_sub: "Every listing we carry, tested against the AAOIFI thresholds — business activity, debt, impermissible income and asset liquidity. No account needed to read the verdict.",
    scr_universe: "Instruments screened",
    scr_compliant: "Shariah compliant",
    scr_questionable: "Questionable",
    scr_noncompliant: "Shariah non-compliant",
    scr_search: "Search a company or ticker",
    scr_all_verdicts: "All verdicts",
    scr_all_markets: "All markets",
    scr_all_sectors: "All sectors",
    scr_sort: "Sort",
    scr_screening: "Screening…",
    scr_matches: "matches",
    scr_pct_compliant: "Shariah compliant",
    scr_company: "Company",
    scr_verdict: "Verdict",
    scr_price: "Price",
    scr_24h: "24h",
    scr_debt: "Debt",
    scr_impure: "Impure income / purify",
    scr_mcap: "Market cap",
    scr_loading: "Screening the universe…",
    scr_empty: "Nothing matched those filters. Try widening the market or verdict.",
    scr_page: "Page",
    scr_of: "of",
    scr_prev: "Previous",
    scr_next: "Next",
    scr_cta_title: "Found a name you like?",
    scr_cta_sub: "Open an account to buy it fractionally, keep it under a monthly Shariah re-screen, and have the purification worked out for you.",
    scr_footnote: "Screening follows the AAOIFI thresholds set by our Shariah Supervisory Board: interest-bearing debt under 30% of market capitalisation, impermissible income under 5% of revenue, and illiquid assets above 20% of total assets, after the business-activity test.",

    // stock verdict
    stk_back: "All halal stocks",
    stk_today: "today",
    stk_share: "Share this verdict",
    stk_why: "Why this verdict",
    stk_why_pass: "clears the business-activity test and all three financial thresholds set by our Shariah board.",
    stk_why_fail: "fails at least one of the thresholds below. The exact numbers are shown so you can judge for yourself.",
    stk_purification_factor: "Purification factor",
    stk_tax_at_source: "Dividend tax at source",
    stk_source: "Source",
    stk_source_filings: "company filings",
    stk_last_120: "Last 120 days",
    stk_ruling_eyebrow: "Relevant board ruling",
    stk_all_rulings: "See all rulings",
    stk_key_figures: "Key figures",
    stk_mcap: "Market cap",
    stk_open: "Open",
    stk_high_52w: "52-week high",
    stk_low_52w: "52-week low",
    stk_volume: "Volume",
    stk_buy_title: "Buy it on Tharwy",
    stk_buy_sub: "Fractional orders from a few riyals, a monthly Shariah re-screen on everything you hold, and purification calculated per dividend.",
    stk_open_account: "Open an account",
    stk_peers: "Shariah compliant names in",
    stk_purify: "purify",
    stk_browse: "Browse the screener",
    stk_screening: "Screening",
    stk_share_card_note: "The card is generated live from the latest filings, so what you send is what we screened.",
    stk_whatsapp: "WhatsApp",
    stk_copy: "Copy link",
    stk_save_card: "Save card",
    stk_link_copied: "Link copied",
    stk_footnote: "Verdicts move with the balance sheet: this page reflects the latest filings we hold, and holdings inside Tharwy are re-screened every 30 days.",

    // leaderboards
    lb_eyebrow: "Sector leaderboards",
    lb_h1a: "The cleanest halal names,",
    lb_h1b: "ranked by sector.",
    lb_ranked_default: "Ranked by the least impermissible income, then the least interest-bearing debt.",
    lb_sub_tail: "Every name below is Shariah compliant on all four AAOIFI screens.",
    lb_screened: "Screened",
    lb_compliant: "Shariah compliant",
    lb_pass_rate: "Pass rate",
    lb_loading: "Ranking the universe…",
    lb_cleanest_ten: "Cleanest ten overall",
    lb_every_market: "every market",
    lb_impure: "impure",
    lb_debt: "debt",
    lb_pass_of: "pass · avg debt",
    lb_of: "of",
    lb_full_screener: "Open the full screener",
    lb_how_we_screen: "How we screen",

    // chart lens
    cl_eyebrow: "Chart Lens",
    cl_h1a: "Paste any chart.",
    cl_h1b: "Get the read in seconds.",
    cl_sub: "Screenshot a chart from anywhere — your broker, a news site, a group chat — and Chart Lens names the trend, the levels that matter, the patterns forming and what would prove the read wrong. Technical analysis only, never a Shariah verdict.",
    cl_drop: "Drop a chart screenshot here",
    cl_drop_hint: "or click to choose · paste with ⌘V · PNG, JPEG, WebP up to 6 MB",
    cl_ticker: "Ticker (optional)",
    cl_note: "Anything specific to look at?",
    cl_run: "Read this chart",
    cl_running: "Reading the chart…",
    cl_clear: "Clear",
    cl_reads_left: "reads left today",
    cl_quota_note: "Five free reads a day without an account, twenty-five when you sign in. Charts are not shared with anyone and are never used to make a recommendation.",
    cl_empty_title: "Nothing read yet",
    cl_empty_sub: "Upload a chart and the read appears here: trend and strength, support and resistance, patterns, momentum, and the things that would invalidate it.",
    cl_loading: "Reading the candles…",
    cl_confidence: "confidence",
    cl_support: "Support",
    cl_resistance: "Resistance",
    cl_none_legible: "None legible.",
    cl_patterns: "Patterns",
    cl_momentum: "Momentum",
    cl_volume: "Volume",
    cl_watch: "What to watch",
    cl_unreadable: "Could not make out",
    cl_is_halal_a: "Is",
    cl_is_halal_b: "halal? Read the verdict",
    cl_err_type: "PNG, JPEG or WebP only",
    cl_err_size: "Keep it under 6 MB",
    cl_read_ok: "Chart read",

    // shariah board
    bd_eyebrow: "Shariah supervisory board",
    bd_h1a: "The scholars who",
    bd_h1b: "set the rules we follow.",
    bd_download_cert: "Download the certificate",
    bd_ask_scroll: "Ask the board a question",
    bd_certificate: "Certificate",
    bd_last_audit: "Last audit",
    bd_review_cycle: "Review cycle",
    bd_next_review: "Next review",
    bd_the_board: "The board",
    bd_since: "since",
    bd_methodology_title: "The screening methodology, in full",
    bd_methodology_sub: "Five stages, applied in order. A name must clear every one of them to show as Shariah Compliant.",
    bd_rulings_title: "Published rulings",
    bd_rulings_sub_a: "Every question the board has settled, with the reasoning behind it.",
    bd_shown_of: "of",
    bd_shown: "shown",
    bd_all: "All",
    bd_basis: "Basis",
    bd_ruled: "ruled",
    bd_meetings: "Meeting record",
    bd_quorum: "Quorum",
    bd_ask_eyebrow: "Ask the board",
    bd_ask_title: "Put a question to the scholars",
    bd_ask_sub: "If your question is not covered above, ask it. The board replies by email, and questions of general interest are published as rulings — never with your name attached.",
    bd_ask_name: "Your name",
    bd_ask_email: "Email for the reply",
    bd_ask_topic: "Topic (optional)",
    bd_ask_question: "Your question — the more detail, the better the answer",
    bd_ask_send: "Send to the board",
    bd_ask_sending: "Sending…",
    bd_footnote: "Rulings apply to the products offered on Tharwy and are not a substitute for a personal ruling on your own circumstances.",

    // learn
    ln_eyebrow: "Tharwy Learn",
    ln_h1a: "Understand it,",
    ln_h1b: "then invest in it.",
    ln_sub: "Plain-English explainers on Islamic finance, Shariah screening, zakat and the tax mechanics of investing globally. No jargon left undefined, no sales pitch in the middle.",
    ln_min_read: "min read",
    ln_min: "min",
    ln_read: "Read",
    ln_cta_title: "Ready to put it into practice?",
    ln_cta_sub: "Open an account, screen any stock before you buy, and keep every holding under a monthly Shariah review.",
    ln_cta_button: "Get started",
    ln_english_note: "",
    art_back: "All articles",
    art_read: "min read",
    art_research: "Tharwy Research",
    art_remember: "What to remember",
    art_disclaimer: "Written for general education. It is not investment, tax or religious advice — for a ruling on your own circumstances, speak to a qualified scholar or adviser.",
    art_keep_reading: "Keep reading",
    art_read_link: "Read",
    art_english_note: "",
    lang_toggle_ar: "العربية",
    lang_toggle_en: "English",
  },
  ar: {
    hl_back: "رجوع",
    hl_read_methodology: "اقرأ المنهجية",
    hl_open_account: "افتح حسابك",
    hl_all_markets: "جميع الأسواق",
    hl_market: "السوق",

    scr_eyebrow: "فارز الأسهم الحلال المجاني",
    scr_h1a: "أفضل الأسهم الحلال،",
    scr_h1b: "مفحوصة بشفافية كاملة.",
    scr_sub: "كل ورقة مالية ندرجها، مُختبرة وفق معايير أيوفي — نشاط الشركة، الديون، الإيراد غير الجائز وسيولة الأصول. لا حاجة لحساب لقراءة الحكم.",
    scr_universe: "الأوراق المفحوصة",
    scr_compliant: "متوافقة مع الشريعة",
    scr_questionable: "مشكوك فيها",
    scr_noncompliant: "غير متوافقة مع الشريعة",
    scr_search: "ابحث عن شركة أو رمز",
    scr_all_verdicts: "جميع الأحكام",
    scr_all_markets: "جميع الأسواق",
    scr_all_sectors: "جميع القطاعات",
    scr_sort: "ترتيب",
    scr_screening: "جارٍ الفحص…",
    scr_matches: "نتيجة مطابقة",
    scr_pct_compliant: "متوافقة مع الشريعة",
    scr_company: "الشركة",
    scr_verdict: "الحكم",
    scr_price: "السعر",
    scr_24h: "٢٤ ساعة",
    scr_debt: "الديون",
    scr_impure: "الإيراد غير الجائز / التطهير",
    scr_mcap: "القيمة السوقية",
    scr_loading: "جارٍ فحص جميع الأوراق…",
    scr_empty: "لا نتائج لهذه المرشحات. جرّب توسيع السوق أو الحكم.",
    scr_page: "صفحة",
    scr_of: "من",
    scr_prev: "السابق",
    scr_next: "التالي",
    scr_cta_title: "أعجبك سهم معين؟",
    scr_cta_sub: "افتح حساباً لتشتريه بأجزاء، ويبقى تحت إعادة فحص شرعي شهري، ونحسب لك مقدار التطهير.",
    scr_footnote: "يتبع الفحص معايير أيوفي التي أقرّها مجلسنا الشرعي: الديون بفائدة أقل من ٣٠٪ من القيمة السوقية، والإيراد غير الجائز أقل من ٥٪ من الإيرادات، والأصول غير السائلة أعلى من ٢٠٪ من إجمالي الأصول، بعد اختبار نشاط الشركة.",

    stk_back: "جميع الأسهم الحلال",
    stk_today: "اليوم",
    stk_share: "شارك هذا الحكم",
    stk_why: "سبب هذا الحكم",
    stk_why_pass: "تجتاز اختبار النشاط التجاري وجميع النسب المالية الثلاث التي أقرّها مجلسنا الشرعي.",
    stk_why_fail: "لا تجتاز واحداً على الأقل من الحدود أدناه. الأرقام معروضة كما هي لتحكم بنفسك.",
    stk_purification_factor: "نسبة التطهير",
    stk_tax_at_source: "ضريبة التوزيعات في المنشأ",
    stk_source: "المصدر",
    stk_source_filings: "إفصاحات الشركة",
    stk_last_120: "آخر ١٢٠ يوماً",
    stk_ruling_eyebrow: "فتوى ذات صلة من المجلس",
    stk_all_rulings: "اطلع على جميع الفتاوى",
    stk_key_figures: "أرقام رئيسية",
    stk_mcap: "القيمة السوقية",
    stk_open: "الافتتاح",
    stk_high_52w: "أعلى ٥٢ أسبوعاً",
    stk_low_52w: "أدنى ٥٢ أسبوعاً",
    stk_volume: "حجم التداول",
    stk_buy_title: "اشترِه عبر ثروي",
    stk_buy_sub: "أوامر جزئية تبدأ من ريالات قليلة، وإعادة فحص شرعي شهرية لكل ما تملكه، وحساب التطهير لكل توزيع.",
    stk_open_account: "افتح حساباً",
    stk_peers: "أسهم متوافقة مع الشريعة في قطاع",
    stk_purify: "تطهير",
    stk_browse: "تصفّح الفارز",
    stk_screening: "جارٍ فحص",
    stk_share_card_note: "تُنشأ البطاقة مباشرة من أحدث الإفصاحات، فما ترسله هو ما فحصناه.",
    stk_whatsapp: "واتساب",
    stk_copy: "انسخ الرابط",
    stk_save_card: "حفظ البطاقة",
    stk_link_copied: "تم نسخ الرابط",
    stk_footnote: "تتغير الأحكام مع الميزانية العمومية: تعرض هذه الصفحة أحدث الإفصاحات المتوفرة لدينا، والحصص داخل ثروي تُعاد فحصها كل ٣٠ يوماً.",

    lb_eyebrow: "لوحات صدارة القطاعات",
    lb_h1a: "أنقى الأسهم الحلال،",
    lb_h1b: "مرتّبة حسب القطاع.",
    lb_ranked_default: "مرتّبة بأقل إيراد غير جائز، ثم بأقل ديون بفائدة.",
    lb_sub_tail: "كل سهم أدناه متوافق مع الشريعة في معايير أيوفي الأربعة.",
    lb_screened: "مفحوصة",
    lb_compliant: "متوافقة مع الشريعة",
    lb_pass_rate: "نسبة الاجتياز",
    lb_loading: "جارٍ ترتيب الأوراق…",
    lb_cleanest_ten: "أنقى عشرة إجمالاً",
    lb_every_market: "جميع الأسواق",
    lb_impure: "إيراد غير جائز",
    lb_debt: "الديون",
    lb_pass_of: "تجتاز · متوسط الديون",
    lb_of: "من",
    lb_full_screener: "افتح الفارز الكامل",
    lb_how_we_screen: "كيف نفحص",

    cl_eyebrow: "عدسة الرسم البياني",
    cl_h1a: "الصق أي رسم بياني.",
    cl_h1b: "واحصل على القراءة في ثوانٍ.",
    cl_sub: "التقط صورة لأي رسم بياني — من وسيطك، أو موقع أخبار، أو محادثة جماعية — وتحدد لك عدسة الرسم البياني الاتجاه، والمستويات المهمة، والأنماط المتشكّلة، وما ينقض القراءة. تحليل فني فقط، وليس حكماً شرعياً.",
    cl_drop: "أفلت صورة الرسم البياني هنا",
    cl_drop_hint: "أو اضغط للاختيار · الصق بـ ⌘V · PNG أو JPEG أو WebP حتى ٦ ميغابايت",
    cl_ticker: "الرمز (اختياري)",
    cl_note: "هل هناك شيء محدد ننظر فيه؟",
    cl_run: "اقرأ هذا الرسم",
    cl_running: "جارٍ قراءة الرسم…",
    cl_clear: "مسح",
    cl_reads_left: "قراءة متبقية اليوم",
    cl_quota_note: "خمس قراءات مجانية يومياً بدون حساب، وخمس وعشرون عند تسجيل الدخول. لا تُشارك الرسوم مع أحد ولا تُستخدم لتقديم توصية.",
    cl_empty_title: "لا قراءة بعد",
    cl_empty_sub: "ارفع رسماً بيانياً وستظهر القراءة هنا: الاتجاه وقوته، الدعم والمقاومة، الأنماط، الزخم، وما ينقض القراءة.",
    cl_loading: "جارٍ قراءة الشموع…",
    cl_confidence: "درجة الثقة",
    cl_support: "الدعم",
    cl_resistance: "المقاومة",
    cl_none_legible: "لا شيء واضح.",
    cl_patterns: "الأنماط",
    cl_momentum: "الزخم",
    cl_volume: "حجم التداول",
    cl_watch: "ما ينبغي مراقبته",
    cl_unreadable: "لم نتمكن من قراءة",
    cl_is_halal_a: "هل",
    cl_is_halal_b: "حلال؟ اقرأ الحكم",
    cl_err_type: "PNG أو JPEG أو WebP فقط",
    cl_err_size: "أقل من ٦ ميغابايت",
    cl_read_ok: "تمت قراءة الرسم",

    bd_eyebrow: "الهيئة الشرعية",
    bd_h1a: "العلماء الذين",
    bd_h1b: "يضعون الأحكام التي نتبعها.",
    bd_download_cert: "تحميل الشهادة",
    bd_ask_scroll: "اطرح سؤالاً على الهيئة",
    bd_certificate: "الشهادة",
    bd_last_audit: "آخر تدقيق",
    bd_review_cycle: "دورة المراجعة",
    bd_next_review: "المراجعة القادمة",
    bd_the_board: "الهيئة",
    bd_since: "منذ",
    bd_methodology_title: "منهجية الفحص بالكامل",
    bd_methodology_sub: "خمس مراحل تُطبّق بالترتيب. على السهم أن يجتازها جميعاً ليظهر متوافقاً مع الشريعة.",
    bd_rulings_title: "الفتاوى المنشورة",
    bd_rulings_sub_a: "كل مسألة حسمتها الهيئة، مع التعليل الذي بُنيت عليه.",
    bd_shown_of: "من",
    bd_shown: "معروضة",
    bd_all: "الكل",
    bd_basis: "الأساس",
    bd_ruled: "صدرت في",
    bd_meetings: "سجل الاجتماعات",
    bd_quorum: "النصاب",
    bd_ask_eyebrow: "اسأل الهيئة",
    bd_ask_title: "اطرح سؤالك على العلماء",
    bd_ask_sub: "إن لم يكن سؤالك مذكوراً أعلاه فاطرحه. تجيب الهيئة عبر البريد الإلكتروني، وتُنشر الأسئلة ذات النفع العام كفتاوى — دون ذكر اسمك أبداً.",
    bd_ask_name: "اسمك",
    bd_ask_email: "البريد الإلكتروني للرد",
    bd_ask_topic: "الموضوع (اختياري)",
    bd_ask_question: "سؤالك — كل تفصيل يزيد دقة الجواب",
    bd_ask_send: "أرسل إلى الهيئة",
    bd_ask_sending: "جارٍ الإرسال…",
    bd_footnote: "تنطبق الفتاوى على المنتجات المتاحة في ثروي، وهي ليست بديلاً عن فتوى خاصة بحالتك.",

    ln_eyebrow: "أكاديمية ثروي",
    ln_h1a: "افهمه أولاً،",
    ln_h1b: "ثم استثمر فيه.",
    ln_sub: "شروحات بلغة واضحة عن المالية الإسلامية، والفحص الشرعي، والزكاة، والأثر الضريبي للاستثمار عالمياً. لا مصطلح دون تفسير، ولا عرض بيعي في المنتصف.",
    ln_min_read: "دقائق قراءة",
    ln_min: "دقيقة",
    ln_read: "اقرأ",
    ln_cta_title: "جاهز للتطبيق؟",
    ln_cta_sub: "افتح حساباً، وافحص أي سهم قبل الشراء، وأبقِ كل حصة تحت مراجعة شرعية شهرية.",
    ln_cta_button: "ابدأ الآن",
    ln_english_note: "نصوص المقالات متاحة حالياً بالإنجليزية فقط.",
    art_back: "جميع المقالات",
    art_read: "دقائق قراءة",
    art_research: "قسم البحوث في ثروي",
    art_remember: "ما ينبغي تذكّره",
    art_disclaimer: "كُتب لغرض التعليم العام، وليس نصيحة استثمارية أو ضريبية أو دينية — ولحكم يخص حالتك، راجع عالماً مؤهلاً أو مستشاراً.",
    art_keep_reading: "تابع القراءة",
    art_read_link: "اقرأ",
    art_english_note: "نص هذا المقال متاح حالياً بالإنجليزية فقط.",
    lang_toggle_ar: "العربية",
    lang_toggle_en: "English",
  },
};

// ---- Arabic mirrors of API-supplied content -------------------------------

export const AR_VERDICT = {
  "Compliant": "متوافق مع الشريعة",
  "Non-Compliant": "غير متوافق مع الشريعة",
  "Questionable": "مشكوك فيه",
  "Permissible": "جائز",
  "Not permissible": "غير جائز",
  "Restricted": "مقيّد",
  "Required": "واجب",
};

export const AR_TOPIC = {
  "All": "الكل",
  "Equities": "الأسهم",
  "Derivatives": "المشتقات",
  "Digital assets": "الأصول الرقمية",
  "Fixed income": "الدخل الثابت",
  "Funds": "الصناديق",
  "Purification": "التطهير",
  "Cash": "النقد",
};

export const AR_SECTOR = {
  "Automotive": "السيارات",
  "Beverages": "المشروبات",
  "Broad Market": "السوق العام",
  "Communication": "الاتصالات والإعلام",
  "Conglomerate": "الشركات المتنوعة",
  "Consumer Disc.": "السلع الكمالية",
  "Emerging Markets": "الأسواق الناشئة",
  "Fixed Income": "الدخل الثابت",
  "Halal Equity": "أسهم حلال",
  "Infrastructure": "البنية التحتية",
  "International": "أسواق دولية",
  "Islamic Banking": "المصرفية الإسلامية",
  "Logistics": "الخدمات اللوجستية",
  "Semiconductors": "أشباه الموصلات",
  "Sukuk": "الصكوك",
  "Technology": "التقنية",
  "Healthcare": "الرعاية الصحية",
  "Energy": "الطاقة",
  "Materials": "المواد الأساسية",
  "Industrials": "الصناعات",
  "Consumer Staples": "السلع الاستهلاكية الأساسية",
  "Consumer Discretionary": "السلع الاستهلاكية الكمالية",
  "Communication Services": "خدمات الاتصالات",
  "Utilities": "المرافق",
  "Real Estate": "العقارات",
  "Financials": "المالية",
  "Telecom": "الاتصالات",
  "Transport": "النقل",
  "Petrochemicals": "البتروكيماويات",
  "Banking": "الخدمات المصرفية",
  "Insurance": "التأمين",
  "Retail": "التجزئة",
  "Basic Materials": "المواد الأساسية",
  "Crypto": "العملات المشفرة",
  "Commodities": "السلع",
  "Forex": "العملات",
  "Index": "المؤشرات",
  "ETF": "صناديق المؤشرات",
};

export const AR_SORT = {
  "Largest first": "الأكبر أولاً",
  "Cleanest income": "أنقى الإيرادات",
  "Lowest debt": "أقل الديون",
  "Biggest movers": "أكبر المتغيرات",
  "A–Z": "أ – ي",
};

export const AR_TREND = {
  Uptrend: "اتجاه صاعد",
  Downtrend: "اتجاه هابط",
  Sideways: "اتجاه عرضي",
};

export const AR_ROLE = { Chairman: "رئيس الهيئة", Member: "عضو", Secretary: "أمين الهيئة" };

export const AR_MANDATE =
  "تضع الهيئة قواعد الفحص التي يطبقها ثروي، وتراجع كل منتج قبل إطلاقه، وتدقّق تطبيق المنصة لتلك القواعد كل ربع سنة، وتصدر الفتوى التي تعمل الخدمة بموجبها.";

export const AR_STATEMENT =
  "تُفحص جميع المنتجات الحلال ربع سنوياً للتحقق من التوافق الشرعي، ويشمل ذلك نشاط الشركة والنسب المالية وتطهير الإيراد غير الجائز العارض.";

export const AR_BOARD_NAME = "الهيئة الشرعية لثروي";

export const AR_METHODOLOGY = {
  "1. Business activity": {
    stage: "١. نشاط الشركة", test: "القطاعات المستثناة",
    detail: "التمويل والتأمين التقليديان، والخمور، والتبغ، والميسر، والترفيه للبالغين، ولحم الخنزير، والأسلحة. الشركة التي يكون نشاطها الأساسي مستثنى لا تنقذها أي نسبة مالية.",
  },
  "2. Interest-bearing debt": {
    stage: "٢. الديون بفائدة", test: "أقل من ٣٠٪ من القيمة السوقية",
    detail: "تُقاس على آخر ميزانية معلنة مقابل متوسط متحرك للقيمة السوقية، حتى لا يقلب يوم واحد شديد التقلب الحكم.",
  },
  "3. Non-permissible income": {
    stage: "٣. الإيراد غير الجائز", test: "أقل من ٥٪ من إجمالي الإيرادات",
    detail: "الفوائد المكتسبة على الودائع مضافاً إليها أي نشاط غير جائز عارض. فوق هذا الحد يُستثنى السهم، وتحته تصبح النسبة معامل التطهير.",
  },
  "4. Illiquid assets": {
    stage: "٤. الأصول غير السائلة", test: "أعلى من ٢٠٪ من إجمالي الأصول",
    detail: "الشركة التي معظم أصولها نقد ومدينون تقترب من مبادلة النقد بالنقد، وهو ما يوجب أن يكون البيع بالمثل.",
  },
  "5. Ongoing review": {
    stage: "٥. المراجعة المستمرة", test: "إعادة فحص كل ٣٠ يوماً",
    detail: "تتحرك النسب مع الميزانية العمومية. تُعاد اختبار كل حصة لدى العملاء شهرياً على الإفصاحات الحديثة، ويُبلَّغ عن أي تخفيض في الحكم في اليوم نفسه.",
  },
};

export const AR_RULING = {
  "conventional-bank-shares": {
    question: "هل يجوز للعميل تملّك أسهم بنك تقليدي؟",
    ruling: "لا. النشاط الأساسي للبنك التقليدي هو الإقراض بفائدة، فيسقط في اختبار النشاط قبل النظر في أي نسبة مالية. ولا يُطهّر هذا الدخل بأي حال، لأن المحظور هو النشاط نفسه وليس جزءاً عارضاً منه.",
    basis: "معيار أيوفي الشرعي رقم ٢١ (الأوراق المالية) — اختبار النشاط التجاري",
  },
  "islamic-banks": {
    question: "وماذا عن أسهم البنوك الإسلامية أو شركات التكافل؟",
    ruling: "جائزة، بشرط اجتيازها النسب المالية نفسها المطبقة على أي شركة مدرجة. فنشاط البنك الإسلامي قائم على التجارة والمشاركة، فيجتاز اختبار النشاط، لكن يبقى هيكل ديونه وودائعه محلاً للاختبار.",
    basis: "معيار أيوفي الشرعي رقم ٢١",
  },
  "options-and-futures": {
    question: "هل يمكن تداول الخيارات أو العقود المستقبلية على المنصة؟",
    ruling: "لا. كلاهما يشتمل على بيع ما لا يُملك ولا يُسلَّم، مع تأجيل الثمن والمثمن في وقت واحد. لذلك تستثني الهيئة الخيارات والعقود المستقبلية وعقود الفروقات التقليدية، ولا يوفرها ثروي على الحساب الإسلامي. أما بيع السلم بوصف السلعة ودفع الثمن كاملاً فمسألة مستقلة ولا تزال قيد المراجعة.",
    basis: "الغرر والنهي عن بيع ما ليس عندك",
  },
  "margin-and-short-selling": {
    question: "هل يجوز الشراء بالهامش أو البيع على المكشوف؟",
    ruling: "لا. الهامش قرض بفائدة، والبيع على المكشوف بيع لأسهم مستعارة. يمنع ثروي الاثنين على الحسابات الإسلامية؛ فلا يُشترى إلا بنقد مسوّى، ولا يُباع إلا سهم مملوك.",
    basis: "الربا في قرض الهامش، وبيع أصل غير مملوك",
  },
  "cryptocurrency": {
    question: "هل العملات المشفرة جائزة؟",
    ruling: "لا تتخذ الهيئة حكماً عاماً. فالعملة الرمزية التي تمثل أصلاً حقيقياً أو خدمة منتفعاً بها قد تكون جائزة؛ وما كان استخدامه الوحيد المضاربة، أو يدفع عائد إقراض، فليس جائزاً. ولذلك تُستثنى العملات المشفرة من الحسابات الإسلامية في ثروي إلى حين مراجعة كل أصل على حدة، ومن يفعّلها فعلى حساب تقليدي.",
    basis: "تقييم كل أصل على حدة من حيث المالية والغرر",
  },
  "conventional-bonds": {
    question: "لماذا تُستثنى السندات الحكومية وسندات الشركات؟",
    ruling: "السند قرض يُسدَّد بفائدة. والصكوك هي البديل الجائز: فهي تفيد ملكية أصل وتدفع ما يدرّه ذلك الأصل من أجرة أو ربح، وتتحمل مخاطره. وتُستثنى صناديق السندات الواسعة للسبب نفسه.",
    basis: "معيار أيوفي الشرعي رقم ١٧ (صكوك الاستثمار)",
  },
  "broad-market-etfs": {
    question: "هل يجوز تملّك صندوق مؤشر واسع مثل مؤشر ستاندرد آند بورز ٥٠٠؟",
    ruling: "لا. فصندوق المؤشر التقليدي يحتوي بنوكاً وشركات تأمين وشركات عالية الرفع المالي دون أي فحص، ولا يستطيع العميل استثناءها. والصناديق المتوافقة مع الشريعة التي تتبع مؤشراً مفحوصاً هي الطريق الجائز لتحقيق التنويع نفسه.",
    basis: "قاعدة النظر إلى المآل: يُحكم على الصندوق بما يحتويه",
  },
  "dividend-purification": {
    question: "ما مقدار ما يجب التخلص منه من التوزيعات؟",
    ruling: "نسبة إيرادات الشركة الآتية من مصادر غير جائزة، مطبَّقة على التوزيع المستلم. يحسب ثروي ذلك لكل حصة ويعرض المبلغ المستحق. ويجب إخراجه دون انتظار أجر، ولا يصح احتسابه خصماً ضريبياً حيث يعده قانون العميل تبرعاً.",
    basis: "معيار أيوفي الشرعي رقم ٢١ — تطهير الإيراد العارض",
  },
  "interest-on-idle-cash": {
    question: "هل يجوز إيداع نقد العميل في حساب بفائدة؟",
    ruling: "لا. تُحفظ أموال العملاء في حسابات منفصلة لا تدر فائدة. وحيث يقيّد أمين الحفظ فائدة لا يمكن ردها، فلا تُمرَّر إلى العملاء وتُصرف في وجوه الخير.",
    basis: "تحريم الربا في الودائع",
  },
  "late-payment-charges": {
    question: "هل يجوز لثروي فرض رسم على تأخر التسوية؟",
    ruling: "يجوز فرض رسم يغطي التكلفة الإدارية الفعلية، ولا يجوز أن يصير دخلاً للشركة. وأي زيادة على التكلفة الموثقة تُصرف في وجوه الخير.",
    basis: "لا يجوز أن يكون الجزاء ربحاً على دين",
  },
};

export const AR_ARTICLE = {
  "murabaha-savings-explained": {
    title: "المرابحة الادخارية… بلا مصطلحات معقدة",
    dek: "إلى أين يذهب مالك فعلاً عند اختيارك خطة ادخار إسلامية، ولماذا يكون العائد ربحاً لا فائدة.",
  },
  "interest-vs-profit": {
    title: "الفائدة والربح: الرقم نفسه، والطريق مختلف",
    dek: "قد يتشابه عائدان تماماً في كشف الحساب ويبقى بينهما فرق جوهري في طريقة الكسب.",
  },
  "aaoifi-screens-explained": {
    title: "معايير أيوفي… حداً حداً",
    dek: "لماذا قد ترسب شركة رابحة وتبدو أخلاقية في الفحص الشرعي — وما تقيسه كل نسبة حقاً.",
  },
  "dividend-purification": {
    title: "تطهير التوزيعات: كم تُخرج، ولماذا",
    dek: "جزء صغير من معظم التوزيعات لم يكن حقاً لك. هذه طريقة حسابه وما يُفعل به.",
  },
  "zakat-on-shares": {
    title: "زكاة الأسهم… محسوبة بأمانة",
    dek: "كونك متداولاً أو مستثمراً طويل الأجل يغيّر ما يجب عليك. الطريقتان مشروحتان بالتفصيل.",
  },
  "dividend-withholding-tax": {
    title: "لماذا وصلك التوزيع أقل مما توقعت؟",
    dek: "تُخصم ضريبة الاستقطاع في بلد الشركة الموزّعة قبل أن يصل المال إلى محفظتك. ما تحتفظ به كل سوق، وما يمكن استرداده.",
  },
};

export const AR_CATEGORY = {
  "All": "الكل",
  "Islamic finance": "المالية الإسلامية",
  "Screening": "الفحص الشرعي",
  "Zakat": "الزكاة",
  "Global investing": "الاستثمار العالمي",
  "Tax": "الضرائب",
};

export const AR_MEMBER = {
  "Sheikh Dr. Abdullah Al-Manea": {
    name: "الشيخ الدكتور عبدالله المنيع",
    credentials: "معتمد من أيوفي",
    country: "السعودية",
    bio: "يرأس الهيئة ويوقّع فتاواها. أربعة عقود في فقه المعاملات، مع تركيز على تطبيق العقود الشرعية الكلاسيكية على الأوراق المالية المدرجة.",
    expertise: ["فقه المعاملات", "فحص الأسهم", "هياكل الصكوك"],
  },
  "Sheikh Dr. Yusuf Al-Shubaily": {
    name: "الشيخ الدكتور يوسف الشبيلي",
    credentials: "باحث في المالية الإسلامية",
    country: "قطر",
    bio: "يراجع منتجات الادخار والمرابحة في السلع على المنصة، ومعالجة أموال العملاء المحفوظة في حسابات منفصلة.",
    expertise: ["المرابحة", "تداول السلع", "الودائع والربا"],
  },
  "Sheikh Dr. Mohammed Al-Ali": {
    name: "الشيخ الدكتور محمد العلي",
    credentials: "فقه المعاملات",
    country: "الكويت",
    bio: "يقود التدقيق الربع سنوي لنتائج الفحص، ويأخذ عينات من الحصص للتأكد من تطبيق الحدود كما هي مقررة.",
    expertise: ["تدقيق الفحص", "التطهير", "الزكاة"],
  },
};

export const AR_MEETING = {
  "2026-01-15": {
    title: "مراجعة الربع الأول ٢٠٢٦",
    summary: "أُقرّ تقييد العملات المشفرة كما صيغ، وصُدّقت سياسة صرف رسوم التأخير في وجوه الخير، واعتُمد تدقيق الفحص للربع الرابع ٢٠٢٥ (عيّنة من ٤٠ حصة، دون أي خلل في تطبيق الحدود).",
    decisions: ["استثناء العملات المشفرة من الحسابات الإسلامية حتى مراجعة كل أصل على حدة",
                "تحديد رسوم التأخير بالتكلفة الموثقة",
                "إصدار الشهادة MWX-SHR-2026-001"],
  },
  "2025-11-12": {
    title: "الفتاوى التأسيسية",
    summary: "وضعت الهيئة حدود الفحص التي يطبقها ثروي، وأصدرت أحكامها على فئات الأدوات المستثناة، واعتمدت معيار أيوفي رقم ٢١ منهجية حاكمة.",
    decisions: ["اعتماد حدود أيوفي: ٣٠٪ للديون، و٥٪ للإيراد غير الجائز، و٢٠٪ للأصول غير السائلة",
                "استثناء المصرفية التقليدية والسندات والخيارات والهامش والبيع على المكشوف",
                "حساب التطهير لكل حصة والإفصاح عنه للعملاء"],
  },
};

export const arMap = (map, value) => (value && map[value]) || value;

// Screening checks and reasons are computed prose from the API; these mirror them in Arabic.
export const AR_CHECK = {
  "Business activity": "نشاط الشركة",
  "Debt ratio": "نسبة الديون",
  "Liquidity ratio": "نسبة السيولة",
  "Receivables ratio": "نسبة المدينين",
  "Non-permissible income": "الإيراد غير الجائز",
  "Illiquid assets": "الأصول غير السائلة",
};

export const arCheckLabel = (label = "") => {
  const [base, suffix] = label.split(" (");
  const ar = AR_CHECK[base] || arMap(AR_SECTOR, base);
  return suffix ? `${ar} (${suffix}` : ar;
};

export const arCheckValue = (v) =>
  v === "not reported" ? "غير معلن" : arMap(AR_SECTOR, v);

const AR_REASON_RULES = [
  [/^Purpose-built Shariah-compliant instrument/, () =>
    "أداة مصممة أصلاً للتوافق مع الشريعة، ومفحوصة عند الإصدار."],
  [/^Core business activity is not permissible/, () =>
    "النشاط الأساسي للشركة غير جائز شرعاً (كالتمويل التقليدي أو الإيرادات المحرمة)."],
  [/^Interest-bearing debt ratio ([\d.]+)% exceeds the (\d+)%/, (m) =>
    `نسبة الديون بفائدة ${m[1]}٪ تتجاوز حد أيوفي البالغ ${m[2]}٪.`],
  [/^Non-permissible income ([\d.]+)% exceeds the (\d+)%/, (m) =>
    `الإيراد غير الجائز ${m[1]}٪ يتجاوز الحد البالغ ${m[2]}٪.`],
  [/^Operates in a gray-area industry/, () =>
    "تعمل في قطاع محل خلاف بين العلماء — يُنصح بالمراجعة."],
  [/^Passes business-activity screen and all AAOIFI/, () =>
    "تجتاز اختبار النشاط التجاري وجميع الحدود المالية في معايير أيوفي."],
  [/^Passes the business-activity screen and every AAOIFI ratio on the latest filed accounts \((.+)\)/, (m) =>
    `تجتاز اختبار النشاط التجاري وكل نسب أيوفي على آخر حسابات معلنة (${m[1]}).`],
  [/^Interest-bearing debt is ([\d.]+)% of market cap \(AAOIFI limit (\d+)%\)/, (m) =>
    `الديون بفائدة ${m[1]}٪ من القيمة السوقية (حد أيوفي ${m[2]}٪).`],
  [/^Cash and interest-bearing securities are ([\d.]+)% of market cap \(limit (\d+)%\)/, (m) =>
    `النقد والأوراق ذات الفائدة ${m[1]}٪ من القيمة السوقية (الحد ${m[2]}٪).`],
  [/^Accounts receivable are ([\d.]+)% of market cap \(limit (\d+)%\)/, (m) =>
    `المدينون ${m[1]}٪ من القيمة السوقية (الحد ${m[2]}٪).`],
  [/^Interest income is ([\d.]+)% of revenue \(limit (\d+)%\)/, (m) =>
    `إيراد الفوائد ${m[1]}٪ من الإيرادات (الحد ${m[2]}٪).`],
  [/^Tangible assets are ([\d.]+)% of total assets \(must exceed (\d+)%\)/, (m) =>
    `الأصول العينية ${m[1]}٪ من إجمالي الأصول (يجب أن تتجاوز ${m[2]}٪).`],
  [/^Within AAOIFI limits but close enough/, () =>
    "داخل حدود أيوفي لكنها قريبة منها بحيث قد يخرجها الإفصاح القادم — يُنصح بالمراجعة."],
  [/^(.+) is ([\d.]+)%, close to the (\d+)% limit\./, (m) =>
    `${AR_CHECK[m[1]] || m[1]} ${m[2]}٪، قريبة من الحد البالغ ${m[3]}٪.`],
];

export const arReason = (text = "") => {
  for (const [re, fn] of AR_REASON_RULES) {
    const m = text.match(re);
    if (m) return fn(m);
  }
  return text;
};

export const AR_SCREENED_BY =
  "الفحص بإشراف الهيئة الشرعية لثروي · معايير أيوفي الشرعية";

export const AR_SOURCE = {
  tharwy_model: "نموذج ثروي",
  yahoo: "ياهو فاينانس",
  yfinance: "ياهو فاينانس",
};
